import * as React from 'react';
import { styled } from '@linaria/react';
import useSound from 'use-sound';

import { BREAKPOINTS } from '@/constants';
import { range, sampleOne } from '@/utils';

const SPRITES: Record<string, [number, number]> = {
  a: [52, 30],
  b: [88, 30],
  c: [124, 30],
  d: [163, 30],
  e: [198, 30],
  // For some weird reason, these two samples don’t work in Safari 🤔. If I shift them up by 10ms, they work, but I don’t want any latency, so I’ll just use the first five samples.
  // f: [236, 30],
  // g: [280, 30],
};

interface Props extends React.HTMLAttributes<HTMLInputElement> {
  mode: 'single-sample' | 'multi-samples';
}

function SFXSlider({ mode, ...delegated }: Props) {
  const [value, setValue] = React.useState(0.5);
  const id = React.useId().replace(/:/g, '');

  const [playTick] = useSound('/sounds/heavier-tick-sprite.mp3', {
    sprite: SPRITES,
    volume: 0.333,
  });

  return (
    <Wrapper
      style={{ '--value-as-percentage': value * 100 + '%' }}
      {...delegated}
    >
      <Ticks>
        {range(21).map((i) => (
          <Tick key={i} />
        ))}
      </Ticks>
      <Input
        id={id}
        type="range"
        value={value}
        min="0"
        max="1"
        step="0.05"
        onChange={(event) => {
          playTick({
            id:
              mode === 'single-sample'
                ? 'b'
                : sampleOne(Object.keys(SPRITES)),
          });
          setValue(Number(event.target.value));
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --input-height: 24px;
  --handle-size: 24px;
  --track-size: 4px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  width: 350px;
  max-width: 100%;
  /* For the outline: */
  border-radius: 1px;

  @media ${BREAKPOINTS.smAndSmaller} {
    --input-height: 42px;
    --handle-size: 32px;
    --track-size: 8px;
  }

  &:has(input:focus-visible) {
    outline: 2px solid white;
    outline-offset: 4px;
  }
`;

const Ticks = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: calc(var(--handle-size) / 2);
  right: calc(var(--handle-size) / 2);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
`;

const Tick = styled.div`
  position: relative;
  width: 1px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 2px;
    height: 6px;
    border-radius: 2px;
    background: var(--color-gray-300);
  }

  &::before {
    top: 0;

    @media ${BREAKPOINTS.smAndSmaller} {
      top: 6px;
    }
  }

  &::after {
    bottom: 0;

    @media ${BREAKPOINTS.smAndSmaller} {
      bottom: 6px;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  height: var(--input-height);
  cursor: pointer;
  background: transparent;
  -webkit-appearance: none;
  outline: none;

  &::-webkit-slider-runnable-track {
    height: var(--track-size);
    background: linear-gradient(
      to right,
      hsl(210deg 30% 90%) 0% var(--value-as-percentage),
      hsl(210deg 20% 15%) var(--value-as-percentage) 100%
    );
    border-radius: 2px;
    box-shadow: inset 0px 0.5px 1px black;
  }

  &::-moz-range-track {
    height: var(--track-size);
    background: linear-gradient(
      to right,
      hsl(210deg 30% 90%) 0% var(--value-as-percentage),
      hsl(210deg 20% 15%) var(--value-as-percentage) 100%
    );
    border-radius: 2px;
    box-shadow: inset 0px 0.5px 1px black;
  }

  &::-webkit-slider-thumb {
    position: relative;
    width: var(--handle-size);
    height: var(--handle-size);
    background: var(--color-text);
    border: 2px solid var(--color-background);
    border-radius: 100px;
    -webkit-appearance: none;
    appearance: none;
    transform: translateY(-9px);

    @media ${BREAKPOINTS.smAndSmaller} {
      transform: translateY(-12px);
    }
  }

  &::-moz-range-thumb {
    position: relative;
    width: var(--handle-size);
    height: var(--handle-size);
    background: var(--color-text);
    border: 2px solid var(--color-background);
    border-radius: 100px;
    -webkit-appearance: none;
    appearance: none;
    transform: translateY(-9px);
  }
`;

export default React.memo(SFXSlider);
